import {AsyncPipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatCard} from '@angular/material/card';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {Router, UrlSerializer} from '@angular/router';

import {Client, ClientService} from '@app/admin/client.service';
import {AssetService} from '@app/asset';
import {AuthService} from '@app/auth/auth.service';
import {SiteConfigService} from '@app/shared/site-config.service';
import {StorageService} from '@app/shared/storage.service';
import {Role, User} from '@app/shared/user.service';
import * as Sentry from "@sentry/angular";
import {ReCaptchaV3Service} from 'ng-recaptcha-2';

import {Observable, of} from 'rxjs';
import {catchError, filter, map, mergeMap, reduce, switchMap, take} from 'rxjs/operators';

@Component({
    selector: 'frog-landing',
    templateUrl: './landing.component.html',
    styleUrls: ['./landing.component.scss'],
    imports: [MatCard, MatProgressSpinner, AsyncPipe],
})
export class LandingComponent implements OnInit {
    public vm$: Observable<any>;
    public error: string;

    constructor(
        private authService: AuthService,
        private clientService: ClientService,
        private siteConfigService: SiteConfigService,
        private storageService: StorageService,
        private assetService: AssetService,
        private recaptchaService: ReCaptchaV3Service,
        private router: Router,
        private urlSerializer: UrlSerializer,
    ) {}

    ngOnInit(): void {
        this.assetService.clear();

        // Get token from IDP
        this.vm$ = this.authService.getIdpToken()
            .pipe(
                switchMap(token => {
                    // Use token to log in user
                    return this.authService.login(token, 'AUTH0');
                }),
                take(1),
                // From here on, we are figuring out which client to be on and which url to start at
                switchMap(user => {
                    // List all clients for the user
                    let clients$ = this.clientService.getClients();

                    // Check for a redirect url to use
                    // NOTE: This only matches "v/:assetId"
                    const redirectUrl = this.storageService.get('redirectUrl');
                    if (redirectUrl) {
                        // Parse url and try to find an assetId
                        const tree = this.urlSerializer.parse(redirectUrl);
                        const segments = tree.root.children['primary']?.segments;

                        if (segments?.length === 2 && segments[0].path === 'v') {
                            // Check to see what the client is for the asset and if the user has access
                            const assetId = segments[1].path;
                            clients$ = this.assetService.clientForAsset(assetId)
                                .pipe(
                                    map(i => [i])
                                );
                        }
                    }

                    return clients$
                        .pipe(
                            map(clients => ({user, clients}))
                        );
                }),
                map(({user, clients}) => {
                    let clientId: string = null;

                    if (clients.length > 0 && clients[0] === null) {
                        Sentry.captureMessage("null client when trying to determine last client", {
                            extra: {
                                user: user,
                                clientsLength: clients.length
                            }
                        });
                    }

                    const lastClientId = this.storageService.get('client', null);
                    if (lastClientId !== null && clients.find(i => i.id === lastClientId)) {
                        clientId = lastClientId;
                    }
                    else {
                        // Just redirect to first found
                        clientId = clients[0].id;
                    }

                    return {user, clientId};
                }),
                switchMap(({user, clientId}) => {
                    const value = {
                        user: user,
                        clientId: clientId
                    };
                    //
                    if (clientId === null) {
                        // No clients yet, create one
                        return this.create().pipe(
                            map(c => {
                                value.clientId = c.id;
                                return value;
                            })
                        );
                    }

                    return of(value);
                }),
                filter(({user, clientId}) => clientId !== null),
                switchMap(({user, clientId}) => this.setClient(clientId, user)),
                map((url: string[]) => {
                    // Check for an existing redirect, then fall back to last client stream
                    const redirectUrl = this.storageService.pop('redirectUrl');
                    if (redirectUrl) {
                        return [redirectUrl];
                    }

                    return url;
                }),
                map((url: string[]) => {
                    return this.router.navigate(url);
                }),
                catchError((err) => {
                    Sentry.captureMessage("Error when navigating to landing client url", {
                        extra: {
                            error: err
                        }
                    });
                    return of(null);
                })
            );
    }

    private create(): Observable<Client> {
        return this.recaptchaService
            .execute('createClient')
            .pipe(
                mergeMap(token => this.clientService.create(token))
            );
    }

    private setClient(client: string, user: User): Observable<string[]> {
        return this.clientService.setClient(client)
            .pipe(
                mergeMap(() => this.siteConfigService.get()),
                reduce((acc, value) => acc),
                map(siteConfig => {
                    if (user.getRole() == Role.Billing) {
                        return ['/a/billing'];
                    } else {
                        return ['/s', siteConfig.data.primary_stream];
                    }
                })
            );
    }
}
